import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search, FileText, Users, Library, Wand2, Plus, ArrowRight, X, Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Global ⌘K / Ctrl+K / "/" command palette.
 *
 * - Fuzzy jump to any quote by customer name / phone / quote #
 * - Quick actions: New Quote, Smart Quote, Catalogue, Customers
 * - Recent 5 quotes as default suggestions
 *
 * Renders as a top-anchored modal. Toggled via keyboard globally OR by
 * clicking the floating trigger in the top bar.
 */
export default function CommandPalette({ open, onClose, user }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const token = localStorage.getItem("token");

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQ("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 40);
      // Preload recent 5 quotes
      axios
        .get(`${API}/quotations?limit=5`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data?.quotations || [];
          setRecent(list.slice(0, 5));
        })
        .catch(() => setRecent([]));
    }
  }, [open, token]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/quotations?search=${encodeURIComponent(term)}&limit=15`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(res.data) ? res.data : res.data?.quotations || [];
        setResults(list.slice(0, 15));
        setActiveIdx(0);
      } catch {
        setResults([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open, token]);

  const isAdmin = user?.role === "admin" || user?.is_admin;
  const actions = useMemo(
    () =>
      [
        { key: "new", label: "New Quote", hint: "Blank quotation", icon: <Plus size={14} />, onSelect: () => navigate("/new") },
        { key: "smart", label: "Smart Quote", hint: "AI from WhatsApp / voice", icon: <Wand2 size={14} />, onSelect: () => navigate("/smart-quote") },
        { key: "customers", label: "Customers", hint: "Browse customer history", icon: <Users size={14} />, onSelect: () => navigate("/customers") },
        isAdmin && { key: "catalogue", label: "Catalogue Manager", hint: "Rates · stock · packages · stats", icon: <Library size={14} />, onSelect: () => navigate("/admin/catalogue") },
      ].filter(Boolean),
    [navigate, isAdmin]
  );

  const filteredActions = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return actions;
    return actions.filter((a) => a.label.toLowerCase().includes(t) || a.hint.toLowerCase().includes(t));
  }, [actions, q]);

  const items = useMemo(() => {
    const searchList = q.trim().length >= 2 ? results : recent;
    return [
      ...filteredActions.map((a) => ({ type: "action", ...a })),
      ...searchList.map((r) => ({
        type: "quote",
        key: r.id,
        id: r.id,
        label: r.customer_name || "—",
        hint: `#${r.id} · ${r.customer_contact || ""} · ₹${Number(r.total_amount || 0).toLocaleString("en-IN")}`,
        onSelect: () => navigate(`/edit/${r.id}`),
      })),
    ];
  }, [filteredActions, results, recent, q, navigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, items.length - 1)); }
      if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === "Enter") {
        e.preventDefault();
        const it = items[activeIdx];
        if (it) { it.onSelect?.(); onClose?.(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, activeIdx, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-3"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden border border-[#e2e8f0]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 h-12 border-b border-[#e2e8f0]">
          <Search size={16} className="text-[#94a3b8]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, phone, quote # · type a command…"
            className="flex-1 h-full outline-none text-sm bg-transparent"
            data-testid="cmd-input"
          />
          <kbd className="hidden sm:inline-block text-[10px] font-mono text-[#94a3b8] bg-[#f1f5f9] px-1.5 py-0.5 rounded">ESC</kbd>
          <button onClick={onClose} className="p-1 hover:bg-[#f1f5f9] rounded" aria-label="Close">
            <X size={14} className="text-[#64748b]" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#94a3b8]">
              <Sparkles size={16} className="inline mr-1" />
              Nothing matches. Try a customer name or phone.
            </div>
          ) : (
            <>
              {q.trim().length < 2 && (
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#94a3b8] bg-[#f8fafc]">
                  Quick actions
                </div>
              )}
              {items.map((it, i) => {
                const active = i === activeIdx;
                const isAction = it.type === "action";
                return (
                  <button
                    key={`${it.type}-${it.key || i}`}
                    type="button"
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => { it.onSelect?.(); onClose?.(); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left ${
                      active ? "bg-[#fef2f2]" : "bg-white"
                    }`}
                    data-testid={`cmd-item-${i}`}
                  >
                    <span className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                      isAction ? "bg-[#dc2626]/10 text-[#dc2626]" : "bg-[#f1f5f9] text-[#475569]"
                    }`}>
                      {isAction ? it.icon : <FileText size={13} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0f172a] truncate">{it.label}</div>
                      <div className="text-[11px] text-[#64748b] truncate">{it.hint}</div>
                    </span>
                    {active && <ArrowRight size={12} className="text-[#dc2626] flex-shrink-0" />}
                  </button>
                );
              })}
              {q.trim().length < 2 && recent.length > 0 && filteredActions.length > 0 && (
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#94a3b8] bg-[#f8fafc] border-t border-[#e2e8f0]">
                  Recent quotes
                </div>
              )}
            </>
          )}
        </div>
        <div className="border-t border-[#e2e8f0] px-3 py-2 flex items-center gap-3 text-[10px] text-[#94a3b8] bg-[#f8fafc]">
          <span><kbd className="font-mono bg-white border border-[#e2e8f0] px-1 rounded">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono bg-white border border-[#e2e8f0] px-1 rounded">↵</kbd> open</span>
          <span className="ml-auto">Adams · quick jump</span>
        </div>
      </div>
    </div>
  );
}
